import { useCallback, useEffect, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { object, string, number } from 'yup';

import isEmpty from 'lodash/isEmpty';

import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextInput from 'src/components/TextInput';
import IconButton from '@mui/material/IconButton';
import ConfirmModal from 'src/components/ConfirmModal';
import { PencilAdornment } from 'src/utils/components';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  useCreateFieldCategory,
  useUpdateFieldCategory,
  type fieldCategoryParamsType,
} from 'src/pages/SetupPage/api';
import { locale } from 'src/config';
import { checkNumberTyping } from 'src/utils/standards';

import messages from './messages';

const checkParam = { locale };

type FieldCategoryFormProps = {
  create: boolean;
  fieldCategory?: UCM.FieldCategoryType;
};

export const FieldCategoryForm = ({
  create,
  fieldCategory,
}: FieldCategoryFormProps) => {
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const { mutate: createFieldCategory, isPending: isCreating } =
    useCreateFieldCategory();
  const { mutate: updateFieldCategory, isPending: isUpdating } =
    useUpdateFieldCategory();

  const initialValues = {
    nameEn: '',
    nameDe: '',
    position: 0,
  };

  const formik = useFormik<fieldCategoryParamsType>({
    initialValues,
    validateOnChange,
    enableReinitialize: true,
    validationSchema: object().shape({
      nameEn: string().required(),
      nameDe: string().required(),
      position: number().integer().min(0).required(),
    }),
    onSubmit: () => {},
  });

  const { values, errors } = formik;

  // Set the values of the form when the tutorial is opened in edit mode
  useEffect(() => {
    if (fieldCategory && !create && open) {
      formik.setValues({
        nameEn: fieldCategory.name.en,
        position: fieldCategory.position,
        nameDe: fieldCategory.name.de ?? '',
      });
    }
  }, [open, fieldCategory, create]);

  const handleNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const {
        target: { name, value },
      } = event;

      if (checkNumberTyping(value, checkParam)) {
        formik.setFieldValue(name, value);
      }
    },
    [],
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setValidateOnChange(false);
    setOpen(false);
    formik.resetForm();
  }, []);

  const handleSubmit = useCallback(async () => {
    setValidateOnChange(true);

    const errors = await formik.validateForm();
    const isValid = isEmpty(errors);

    if (!isValid) return false;

    return new Promise<boolean>((resolve) => {
      const mutationOptions = {
        onError: () => resolve(false),
        onSuccess: () => resolve(true),
      };

      if (create) {
        return createFieldCategory(values, mutationOptions);
      }

      if (!create && fieldCategory) {
        return updateFieldCategory(
          { ...values, uuid: fieldCategory.uuid },
          mutationOptions,
        );
      }
    });
  }, [values, create, fieldCategory]);

  return (
    <ConfirmModal
      width="700px"
      onOpen={handleOpen}
      onClose={handleClose}
      onConfirm={handleSubmit}
      loading={isCreating || isUpdating}
      title={formatMessage(messages.title)}
      triggerButton={(handleOpen) =>
        create ? (
          <Button
            variant="outlined"
            onClick={handleOpen}
            startIcon={<AddOutlinedIcon color="primary" />}
          >
            {formatMessage(messages.createButton)}
          </Button>
        ) : (
          <Tooltip arrow title={formatMessage(messages.editTooltip)}>
            <IconButton onClick={handleOpen}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            fullWidth
            name="nameEn"
            value={values.nameEn}
            error={!!errors.nameEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.nameEnLabel)}
            helperText={errors.nameEn ?? formatMessage(messages.shownToUsers)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            fullWidth
            name="nameDe"
            value={values.nameDe}
            error={!!errors.nameDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.nameDeLabel)}
            helperText={errors.nameDe ?? formatMessage(messages.shownToUsers)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            fullWidth
            name="position"
            value={values.position}
            error={!!errors.position}
            helperText={errors.position}
            EndIcon={PencilAdornment}
            onChange={handleNumberChange}
            label={formatMessage(messages.positionLabel)}
          />
        </Grid>
      </Grid>
    </ConfirmModal>
  );
};
